import { TutorSupportLevel, BiteStatus, BiteDisplayState } from "./enums";
import type { Range, BiteAttempt, BiteData } from "./data";
import {
  supportCutoff,
  displayStateStyleMap,
  SUPPORT_LEVEL_RANGES,
} from "../constants/config/support";

export const getIncorrectAttemptPercentageRange = (
  level: TutorSupportLevel,
): Range => {
  return SUPPORT_LEVEL_RANGES[level];
};

export const getTutorSupportLevelFromPercentage = (
  percentage: number,
): TutorSupportLevel => {
  for (const [level, range] of Object.entries(SUPPORT_LEVEL_RANGES)) {
    if (percentage >= range.min && percentage <= range.max) {
      return level as TutorSupportLevel;
    }
  }
  return TutorSupportLevel.HIGH;
};

const TUTOR_SUPPORT_LEVEL_ORDER: Record<TutorSupportLevel, number> = {
  [TutorSupportLevel.FLAWLESS]: 0,
  [TutorSupportLevel.LOW]: 1,
  [TutorSupportLevel.MEDIUM]: 2,
  [TutorSupportLevel.HIGH]: 3,
};

export const getTutorSupportLevelOrder = (
  level: TutorSupportLevel | undefined,
): number => {
  return level ? TUTOR_SUPPORT_LEVEL_ORDER[level] : Infinity;
};

export const calculateIncorrectAttemptPercentage = (
  incorrectAttempts: number,
  totalAttempts: number,
): number => {
  if (totalAttempts === 0) return 0;
  return (incorrectAttempts / totalAttempts) * 100;
};

export const getTutorSupportLevelFromPerformance = (
  incorrectAttempts: number,
  totalAttempts: number,
): TutorSupportLevel => {
  const percentage = calculateIncorrectAttemptPercentage(
    incorrectAttempts,
    totalAttempts,
  );
  return getTutorSupportLevelFromPercentage(percentage);
};

export const getTutorSupportLevel = (
  attempt: BiteAttempt,
): TutorSupportLevel | undefined => {
  if (attempt.studentPerformance) {
    return getTutorSupportLevelFromPerformance(
      attempt.studentPerformance.incorrectAttempts,
      attempt.studentPerformance.totalAttempts,
    );
  }
  return undefined;
};

export const getBestAttempt = (attempts: BiteAttempt[]): BiteAttempt => {
  return attempts.reduce((best, attempt) => {
    const bestOrder = getTutorSupportLevelOrder(getTutorSupportLevel(best));
    const attemptOrder = getTutorSupportLevelOrder(
      getTutorSupportLevel(attempt),
    );
    return attemptOrder < bestOrder ? attempt : best;
  }, attempts[0]);
};

// Pastel treatment for the last-viewed bite. A state with no entry here keeps its
// own background and only gains the dashed border.
// border is a color token only — getBiteStyle wraps it in "border border-dashed {border}".
const LAST_LEARNED_BORDER = "border border-dashed border-[#B0B0B0]";
const LAST_LEARNED_OVERRIDES: Partial<
  Record<BiteDisplayState, { bg: string; border: string }>
> = {
  [BiteDisplayState.FLAWLESS]: {
    bg: "bg-[#B8D4FF]",
    border: "border-[#416BBF]",
  },
  [BiteDisplayState.LOW]: { bg: "bg-[#C8E89C]", border: "border-[#5E8C11]" },
  [BiteDisplayState.MEDIUM]: {
    bg: "bg-[#FFD880]",
    border: "border-[#B87400]",
  },
  [BiteDisplayState.HIGH]: { bg: "bg-[#FFB3B3]", border: "border-[#CA0000]" },
  [BiteDisplayState.NOT_ANALYZED]: {
    bg: "bg-[#86EFAC]",
    border: "border-[#006400]",
  },
};

type BiteStyleResult = {
  backgroundColor: string;
  border: string;
  tagColor: string;
  caretColor: string;
};

const LEVEL_DISPLAY_STATE: Record<TutorSupportLevel, BiteDisplayState> = {
  [TutorSupportLevel.FLAWLESS]: BiteDisplayState.FLAWLESS,
  [TutorSupportLevel.LOW]: BiteDisplayState.LOW,
  [TutorSupportLevel.MEDIUM]: BiteDisplayState.MEDIUM,
  [TutorSupportLevel.HIGH]: BiteDisplayState.HIGH,
};

const toDisplayState = (
  status: BiteStatus,
  level: TutorSupportLevel | undefined,
): BiteDisplayState => {
  if (level) return LEVEL_DISPLAY_STATE[level];
  // The only case left for a completed bite: it finished, but no measurement
  // landed for it.
  if (status === BiteStatus.COMPLETED) return BiteDisplayState.NOT_ANALYZED;
  if (status === BiteStatus.NOT_STARTED) return BiteDisplayState.NOT_STARTED;
  return BiteDisplayState.INCOMPLETE;
};

/**
 * Which display state a bite — or one of its attempts — reads as. This picks the
 * (biteStatus, tutorSupportLevel) pair to judge by; toDisplayState turns that
 * pair into a state.
 */
export const resolveDisplayState = (
  bite: BiteData,
  attempt?: BiteAttempt,
): BiteDisplayState => {
  if (attempt) {
    return toDisplayState(attempt.biteStatus, attempt.tutorSupportLevel);
  }
  if (bite.isProcessing) {
    return BiteDisplayState.PROCESSING;
  }
  if (bite.tutorSupportLevel) {
    return toDisplayState(bite.biteStatus, bite.tutorSupportLevel);
  }
  if (bite.attempts.length > 0) {
    const best = getBestAttempt(bite.attempts);
    return toDisplayState(
      best.biteStatus,
      best.tutorSupportLevel ?? getTutorSupportLevel(best),
    );
  }
  return toDisplayState(bite.biteStatus, undefined);
};

/**
 * Single source of truth for bite color logic — used by both Bite.tsx (tile)
 * and BiteCard (modal indicator) to guarantee 1:1 color parity.
 */
export const getBiteStyle = (
  bite: BiteData,
  attempt?: BiteAttempt,
): BiteStyleResult => {
  const state = resolveDisplayState(bite, attempt);
  const baseStyle = displayStateStyleMap[state];
  // Overrides never carry PROCESSING's animation, so this only ever applies
  // when backgroundColor falls back to baseStyle.backgroundColor below.
  const animationClass = baseStyle.animation ? ` ${baseStyle.animation}` : "";

  if (bite.isLastLearned) {
    const override = LAST_LEARNED_OVERRIDES[state];
    return {
      backgroundColor:
        (override?.bg ?? baseStyle.backgroundColor) + animationClass,
      border: override
        ? `border border-dashed ${override.border}`
        : LAST_LEARNED_BORDER,
      tagColor: baseStyle.tagColor,
      caretColor: baseStyle.caretColor,
    };
  }

  return {
    backgroundColor: baseStyle.backgroundColor + animationClass,
    border: "",
    tagColor: baseStyle.tagColor,
    caretColor: baseStyle.caretColor,
  };
};

export const calculateNeedsMoreSupport = (bites: BiteData[]): boolean => {
  const percentage = bites.reduce((acc, bite) => {
    if (bite.attempts.length === 0) return acc;
    const bestAttempt = getBestAttempt(bite.attempts);
    const tutorSupportLevel = getTutorSupportLevel(bestAttempt);
    return (
      acc +
      (tutorSupportLevel === TutorSupportLevel.HIGH ? 1 : 0) / bites.length
    );
  }, 0);
  return percentage >= supportCutoff;
};
